import { Container, Point } from 'pixi.js';
import { FXParticleLayer } from '../../../libs/particles/FXParticleLayer';
import type { ResourceAnimationProperties } from './types';
import { createResourceAnimationSprites } from './createResourceAnimationSprites';
import CoinHitCounterParticlesJSON from '../../../../assets/particles{ignore}/Coin_Hit_Counter_Particles.json';
import type { FXParticleEmitterConfig } from '../../../libs/particles/FXParticleEmitterConfig';
import { removeElement } from '../../../utils/array-utils';
import { repeatUntilFalse } from '../../../utils/async-utils';
import type { ResourceAnimationSprite } from './ResourceAnimationSprite';

export class ResourceAnimationsView extends Container {
  public readonly particleLayer = new FXParticleLayer();

  private readonly activeAnimations: ResourceAnimationSprite[] = [];

  constructor() {
    super();
    this.addChild(this.particleLayer);
  }

  public animateCoins(
    from: Container,
    to: Container,
    animationOverrides?: Partial<ResourceAnimationProperties>,
    onAnimationComplete?: () => void,
  ): void {
    this.animateParticles(from, to, 'coin', animationOverrides, onAnimationComplete);
  }

  public animateUnlimitedLives(
    from: Container,
    to: Container,
    animationOverrides?: Partial<ResourceAnimationProperties>,
    onAnimationComplete?: () => void,
  ): void {
    this.animateParticles(from, to, 'Reward_Infinite_Lives', animationOverrides, onAnimationComplete);
  }

  private animateParticles(
    from: Container,
    to: Container,
    texture: string,
    animationOverrides?: Partial<ResourceAnimationProperties>,
    onAnimationComplete?: () => void,
  ): void {
    const fromPoint = new Point();
    const toPoint = new Point();
    const fromLocal = this.toLocal(fromPoint, from, fromPoint);
    const toLocal = this.toLocal(toPoint, to, toPoint);

    const sprites = createResourceAnimationSprites({
      parent: this,
      particleLayer: this.particleLayer,
      emitterConfig: CoinHitCounterParticlesJSON as FXParticleEmitterConfig,
      from: fromLocal,
      to: toLocal,
      spriteFrames: [texture],
      spriteCount: 5,
      ease: 'quadInOut',
      minDuration: 700,
      maxDuration: 1000,
      delayIncrement: 20,
      minScale: 0.85,
      maxScale: 1,
      ...animationOverrides,
      onComplete: (): void => {
        sprites.forEach((sprite) => {
          removeElement(this.activeAnimations, sprite);
        });
        onAnimationComplete?.();
      },
    });

    this.activeAnimations.push(...sprites);
  }

  public stopAnimations(): void {
    this.activeAnimations.slice().forEach((sprite) => {
      sprite.stopAnimation();
    });
  }

  public async waitUntilAnimationsComplete(): Promise<void> {
    await repeatUntilFalse(() => this.activeAnimations.length > 0);
  }

  public update(deltaTime: number): void {
    this.particleLayer.update(deltaTime);
  }

  public destroy(): void {
    super.destroy({ children: true });
  }
}
